<script setup lang="ts">
import { useBillingUtils } from '@/composables/useBillingUtils';
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { Badge, Button, LayoutGap, Loader, Prose } from '@dabble/quillui';
import { format } from 'date-fns';
import { computed, onMounted, ref, watch } from 'vue';

interface InvoiceLineItem {
  id: string;
  description?: string | null;
}
interface Invoice {
  id: string;
  created: number;
  total: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | null;
  hosted_invoice_url?: string | null;
  lines: { data: InvoiceLineItem[] };
  auto_advance?: boolean | null;
}

const billingStore = useBillingStore();
const { formatPrice } = useBillingUtils();
const { t } = useI18n();
const { showToast } = useToast();

const localInvoices = ref<Invoice[]>([]);
const loadingMore = ref(false);
const noMoreInvoices = ref(false);
const retrying = ref<Record<string, boolean>>({});
const initialLoadDone = ref(false);

onMounted(() => {
  if (!billingStore.invoices) return;
  localInvoices.value = billingStore.invoices as unknown as Invoice[];
  noMoreInvoices.value = localInvoices.value.length < 3;
  initialLoadDone.value = true;
});

// Keep localInvoices in sync with the store after refreshes (e.g. retry, pause-with-void).
// Patches matching entries by id and prepends any brand-new invoices from the store.
watch(
  () => billingStore.invoices,
  (storeInvoices) => {
    if (!storeInvoices) return;
    const store = storeInvoices as unknown as Invoice[];
    const storeById = new Map(store.map((i) => [i.id, i]));
    const localIds = new Set(localInvoices.value.map((i) => i.id));
    const patched = localInvoices.value.map((i) => storeById.get(i.id) ?? i);
    const newOnes = store.filter((i) => !localIds.has(i.id));
    localInvoices.value = newOnes.length ? [...newOnes, ...patched] : patched;
  },
);

const invoices = computed(() => localInvoices.value);
const isMobile = ref(false);

const statusColors: Record<string, 'info' | 'success' | 'danger' | 'warning' | 'neutral'> = {
  void: 'info',
  uncollectible: 'info',
  draft: 'info',
  paid: 'success',
  open: 'danger',
};

async function loadMoreInvoices() {
  if (loadingMore.value || noMoreInvoices.value || localInvoices.value.length === 0) return;

  loadingMore.value = true;
  const lastInvoiceId = localInvoices.value[localInvoices.value.length - 1]?.id;
  if (!lastInvoiceId) {
    loadingMore.value = false;
    return;
  }

  try {
    const fetched = (await billingStore.fetchInvoices(lastInvoiceId)) as unknown as Invoice[];
    if (fetched.length < 5) noMoreInvoices.value = true;
    const seen = new Set(localInvoices.value.map((i) => i.id));
    const newOnes = fetched.filter((i) => !seen.has(i.id));
    if (newOnes.length) {
      localInvoices.value = [...localInvoices.value, ...newOnes];
    } else {
      noMoreInvoices.value = true;
    }
  } catch (err) {
    logError('Failed to load more invoices:', err);
    showToast('danger', t('billing_error_loading_invoices'));
  } finally {
    loadingMore.value = false;
  }
}

async function retryPayment(invoice: Invoice) {
  retrying.value = { ...retrying.value, [invoice.id]: true };
  try {
    const updated = (await billingStore.retryInvoicePayment(invoice.id)) as unknown as Invoice | null;
    if (updated) {
      localInvoices.value = localInvoices.value.map((i) => (i.id === updated.id ? updated : i));
    }
  } catch (err) {
    logError('Failed to retry invoice payment:', err);
    showToast('danger', t('billing_error_retry_payment'));
  } finally {
    const newRetrying = { ...retrying.value };
    delete newRetrying[invoice.id];
    retrying.value = newRetrying;
  }
}

function getStatus(invoice: Invoice): keyof typeof statusColors {
  if (!invoice.auto_advance && invoice.status === 'open') return 'void';
  return invoice.status || 'void';
}

function formatInvoiceDate(timestamp: number): string {
  const formatString = isMobile.value ? 'P' : 'PP';
  return format(new Date(timestamp * 1000), formatString);
}

function getInvoiceDescription(invoice: Invoice): string {
  const description = invoice.lines?.data?.[invoice.lines.data.length - 1]?.description;
  return description?.replace(/^\d+ × | \(at .[\d.]+ \/ \w+\)$/g, '') || '';
}

function openInvoice(url: string | null | undefined) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}
</script>

<template>
  <LayoutGap direction="column" size="none" fill class="invoices-root">
    <div :class="['invoices', { small: isMobile }]">
      <template v-if="initialLoadDone && invoices.length === 0">
        <div class="invoices-empty">
          {{ t('billing_invoice_none') }}
        </div>
      </template>

      <template v-else>
        <div v-for="invoice in invoices" :key="invoice.id" class="invoice-row">
          <div class="invoice-cell invoice-created">
            <Button
              variant="primary"
              finish="link"
              icon-right="open-in-new"
              :disabled="!invoice.hosted_invoice_url"
              @click="openInvoice(invoice.hosted_invoice_url)"
            >
              {{ formatInvoiceDate(invoice.created) }}
            </Button>
          </div>
          <div class="invoice-cell invoice-amount">
            <Prose>{{ formatPrice(invoice.total, invoice.currency, true) }}</Prose>
          </div>
          <div class="invoice-cell invoice-status">
            <LayoutGap direction="row" vertical-align="center" size="small">
              <Button
                v-if="getStatus(invoice) === 'open'"
                variant="secondary"
                finish="outlined"
                size="small"
                icon-left="billing"
                :disabled="!!retrying[invoice.id]"
                @click="retryPayment(invoice)"
              >
                {{ t('billing_invoice_retry') }}
              </Button>
              <Badge :variant="statusColors[getStatus(invoice)]">
                {{ t(`billing_invoice_${getStatus(invoice)}`) }}
              </Badge>
            </LayoutGap>
          </div>
          <Prose variant="secondary" size="small" class="invoice-description">
            {{ getInvoiceDescription(invoice) }}
          </Prose>
        </div>

        <div v-if="loadingMore || !noMoreInvoices" class="load-more">
          <Loader v-if="loadingMore" />
          <Button v-else variant="primary" icon-left="plus" :disabled="loadingMore" @click="loadMoreInvoices">
            {{ t('billing_invoice_load_more') }}
          </Button>
        </div>
      </template>
    </div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.invoices {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.invoice-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  align-items: center;
}

.invoice-description {
  grid-column: 1 / -1;
}

.load-more {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}
</style>
